import { LabelWithHighlights } from '@euler/app/components/LabelWithHighlights';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Assignee } from '@euler/model/task-detail';
import { FuseResultMatch } from 'fuse.js';
import { memo } from 'react';
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const WorkItemCell = memo(
  ({
    title,
    matches,
    assignee,
    showAssignee,
    selected,
    checked,
    deletable,
    style,
    titleStyle,
    onPress,
    onRemove,
    onAdd,
    onDelete,
  }: {
    title: string;
    matches?: ReadonlyArray<FuseResultMatch>;
    selected?: boolean;
    checked?: boolean;
    deletable?: boolean;
    showAssignee?: boolean;
    assignee?: Assignee;
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
    onPress?: () => void;
    onRemove?: () => void;
    onAdd?: () => void;
    onDelete?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          min-height: ${rpx(44)}px;
          border-radius: ${rpx(22)}px;
          padding-left: ${rpx(15)}px;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
        style={style}
        onPress={onPress}
      >
        {showAssignee ? (
          <Icon
            name={checked ? 'checkbox' : 'checkbox_outline'}
            color={checked ? 'rgba(32, 127, 231, 1)' : '#777'}
            size={rpx(20)}
            css={`
              margin-right: ${rpx(5)}px;
              align-self: flex-start;
              transform: translateY(${rpx(3.3)}px);
            `}
          />
        ) : null}
        <View
          css={`
            flex: 1;
          `}
        >
          <HStack justifyContent="flex-start">
            {deletable ? (
              <TouchableOpacity
                onPress={onDelete}
                css={`
                  height: ${rpx(44)}px;
                  width: ${rpx(20)}px;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                  flex-grow: 0;
                  margin-right: ${rpx(4)}px;
                `}
                hitSlop={PredefinedHitSlops.large}
              >
                <Icon name="closecircle" color={'#ccc'} size={rpx(18)} />
              </TouchableOpacity>
            ) : null}
            <LabelWithHighlights
              title={title}
              matches={matches}
              style={titleStyle}
              medium
              size={rpx(15)}
              numberOfLines={1}
            />
          </HStack>
          {showAssignee ? (
            <HStack
              justifyContent="flex-start"
              css={`
                padding-bottom: ${rpx(4)}px;
              `}
            >
              <Typography.Label
                size={rpx(5)}
                lineHeight={rpx(20)}
                css={`
                  margin-right: ${rpx(4)}px;
                `}
              >
                {assignee ? '🟢' : '🟠'}
              </Typography.Label>
              <Typography.Label
                light
                size={rpx(13)}
                lineHeight={rpx(20)}
                color={'#666'}
              >
                {'施工技师: '}
              </Typography.Label>
              <Typography.Label
                light
                size={rpx(13)}
                lineHeight={rpx(20)}
                color={assignee ? '#666' : StatusColors.Danger}
              >
                {assignee?.name ?? '未派工'}
              </Typography.Label>
            </HStack>
          ) : null}
        </View>
        <TouchableOpacity
          onPress={selected ? onRemove : onAdd}
          css={`
            height: ${rpx(44)}px;
            width: ${rpx(50)}px;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            flex-grow: 0;
          `}
          hitSlop={PredefinedHitSlops.large}
        >
          <Icon
            name={selected ? 'minus-circle' : 'plus-circle'}
            color={selected ? StatusColors.Danger : StatusColors.Info}
            size={rpx(24)}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  },
);
